import classNames from 'classnames'
import { TRevenueSnapshotKeys } from 'legacy/lut/classes/models/partner360/ELT/IELTFinancialData'
import { IRevenue } from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { getFieldByActualsValueType } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getFieldByActualsValueType'

import { Translation } from 'cng-common/features/translations/components/translation'
import { FinancialBeautifiedValue } from 'partner360/components/financialBeautifiedValue/FinancialBeautifiedValue'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { getFormedEltTranslation } from '../../../helpers/getFormedEltTranslation'
import { getPercentView } from '../../../helpers/getPercentView'
import { handleNumberColumnChange } from '../../../helpers/handleNumberColumnChange'
import { FinancialInput } from '../../styledComponents/financialInput/FinancialInput'
import { IColumn } from '../../styledComponents/styledTable/types'
import { IIsInconsistencyPayload } from '../models'
import { RenderYearResultColumn } from '../renderYearResultColumn/RenderYearResultColumn'
import styles from '../Revenue.module.scss'

import {
  getCurrentYearQuarterThView,
  getCurrentYearThViewTotal,
  getPrevYearQuarterThView,
  getPrevYearThViewTotal,
  getTwoYearsAgoThTotal,
} from './getDynamicThView'
import { isTotalRowsColumn } from './isTotalRowsColumn'

interface IProps {
  onChange: (field: string, keyValue: string) => (value: string | number) => void
  currentYear: number
  currentFQuarter: number
  isInconsistency: (args: IIsInconsistencyPayload) => boolean
  isDisabled?: boolean
  yearFromJbpSettings: number
  netNdpValue: ActualsValueTypes
}

export const getRevenueFullViewColumns = ({
  onChange,
  currentYear,
  currentFQuarter,
  isInconsistency,
  isDisabled,
  netNdpValue,
}: IProps): IColumn<IRevenue>[] => {
  const getField = (field: string) =>
    getFieldByActualsValueType<IRevenue>(field, netNdpValue) as keyof IRevenue

  const twoYearsAgoField = getField('TwoYearsAgo')

  const prevYearQuarter1Field = getField('PrevYearQuarter1')
  const prevYearQuarter2Field = getField('PrevYearQuarter2')
  const prevYearQuarter3Field = getField('PrevYearQuarter3')
  const prevYearQuarter4Field = getField('PrevYearQuarter4')
  const previousYearRevenueField = getField('PreviousYearRevenue')

  const currentYearQuarter1Field = getField('CurrentYearQuarter1')
  const currentYearQuarter2Field = getField('CurrentYearQuarter2')
  const currentYearQuarter3Field = getField('CurrentYearQuarter3')
  const currentYearQuarter4Field = getField('CurrentYearQuarter4')
  const currentYearRevenueField = getField('CurrentYearRevenue')

  const yoyField = getField('YoY')

  return [
    {
      title: getFormedEltTranslation('revenue-table-revenue'),
      field: 'Name',
      width: 100,
      renderComponent: ({ value, keyValue }) => (
        <div
          className={classNames(styles.textItem, styles.alignColumns, {
            [styles['total']]: isTotalRowsColumn(keyValue, isDisabled),
          })}
        >
          <Translation text={value as string} />
        </div>
      ),
    },
    {
      titleRenderFn: () => getTwoYearsAgoThTotal(currentYear),
      field: twoYearsAgoField,
      renderComponent: ({ value, keyValue }) => (
        <FinancialBeautifiedValue
          value={value}
          className={classNames(styles.textItem, styles.alignColumns, {
            [classNames(styles['total'], styles.totalColumn, styles.column)]: isTotalRowsColumn(
              keyValue,
              isDisabled
            ),
          })}
        />
      ),
    },
    {
      field: prevYearQuarter1Field,
      titleRenderFn: () =>
        getPrevYearQuarterThView({ quarter: 1, currentQuarter: currentFQuarter, currentYear }),
      renderComponent: ({ value, keyValue }) => {
        if (isTotalRowsColumn(keyValue, isDisabled)) {
          return (
            <FinancialBeautifiedValue
              value={value}
              className={classNames(styles.textItem, styles.alignColumns, {
                [styles['total']]: isTotalRowsColumn(keyValue, isDisabled),
              })}
            />
          )
        }

        return (
          <FinancialInput
            showOnlyBeautifiedValue={isDisabled}
            type="number"
            value={value || ''}
            className={styles.alignColumns}
            onChange={handleNumberColumnChange({
              field: prevYearQuarter1Field,
              keyValue,
              onChange,
            })}
          />
        )
      },
    },
    {
      field: prevYearQuarter2Field,
      titleRenderFn: () =>
        getPrevYearQuarterThView({ quarter: 2, currentQuarter: currentFQuarter, currentYear }),
      renderComponent: ({ value, keyValue }) => {
        if (isTotalRowsColumn(keyValue, isDisabled)) {
          return (
            <FinancialBeautifiedValue
              value={value}
              className={classNames(styles.textItem, styles.alignColumns, {
                [styles['total']]: isTotalRowsColumn(keyValue, isDisabled),
              })}
            />
          )
        }

        return (
          <FinancialInput
            showOnlyBeautifiedValue={isDisabled}
            type="number"
            value={value || ''}
            className={styles.alignColumns}
            onChange={handleNumberColumnChange({
              field: prevYearQuarter2Field,
              keyValue,
              onChange,
            })}
          />
        )
      },
    },
    {
      field: prevYearQuarter3Field,
      titleRenderFn: () =>
        getPrevYearQuarterThView({ quarter: 3, currentQuarter: currentFQuarter, currentYear }),
      renderComponent: ({ value, keyValue }) => {
        if (isTotalRowsColumn(keyValue, isDisabled)) {
          return (
            <FinancialBeautifiedValue
              value={value}
              className={classNames(styles.textItem, styles.alignColumns, {
                [styles['total']]: isTotalRowsColumn(keyValue, isDisabled),
              })}
            />
          )
        }

        return (
          <FinancialInput
            showOnlyBeautifiedValue={isDisabled}
            type="number"
            value={value || ''}
            className={styles.alignColumns}
            onChange={handleNumberColumnChange({
              field: prevYearQuarter3Field,
              keyValue,
              onChange,
            })}
          />
        )
      },
    },
    {
      field: prevYearQuarter4Field,
      titleRenderFn: () =>
        getPrevYearQuarterThView({ quarter: 4, currentQuarter: currentFQuarter, currentYear }),
      renderComponent: ({ value, keyValue }) => {
        if (isTotalRowsColumn(keyValue, isDisabled)) {
          return (
            <FinancialBeautifiedValue
              value={value}
              className={classNames(styles.textItem, styles.alignColumns, {
                [styles['total']]: isTotalRowsColumn(keyValue, isDisabled),
              })}
            />
          )
        }

        return (
          <FinancialInput
            showOnlyBeautifiedValue={isDisabled}
            type="number"
            value={value || ''}
            className={styles.alignColumns}
            onChange={handleNumberColumnChange({
              field: prevYearQuarter4Field,
              keyValue,
              onChange,
            })}
          />
        )
      },
    },
    {
      field: previousYearRevenueField,
      titleRenderFn: () => getPrevYearThViewTotal(currentYear),
      renderComponent: ({ value, keyValue }) => (
        <RenderYearResultColumn
          isDisabled={isDisabled}
          keyValue={keyValue as TRevenueSnapshotKeys}
          isInconsistency={isInconsistency}
          yearField={previousYearRevenueField}
          quarterFields={[
            prevYearQuarter1Field,
            prevYearQuarter2Field,
            prevYearQuarter3Field,
            prevYearQuarter4Field,
          ]}
          value={value}
        />
      ),
    },
    {
      field: currentYearQuarter1Field,
      titleRenderFn: () =>
        getCurrentYearQuarterThView({ quarter: 1, currentQuarter: currentFQuarter, currentYear }),
      renderComponent: ({ value, keyValue }) => {
        if (isTotalRowsColumn(keyValue, isDisabled)) {
          return (
            <FinancialBeautifiedValue
              value={value}
              className={classNames(styles.textItem, styles.alignColumns, {
                [styles['total']]: isTotalRowsColumn(keyValue, isDisabled),
              })}
            />
          )
        }

        return (
          <FinancialInput
            showOnlyBeautifiedValue={isDisabled}
            type="number"
            value={value || ''}
            className={styles.alignColumns}
            onChange={handleNumberColumnChange({
              field: currentYearQuarter1Field,
              keyValue,
              onChange,
            })}
          />
        )
      },
    },
    {
      field: currentYearQuarter2Field,
      titleRenderFn: () =>
        getCurrentYearQuarterThView({ quarter: 2, currentQuarter: currentFQuarter, currentYear }),
      renderComponent: ({ value, keyValue }) => {
        if (isTotalRowsColumn(keyValue, isDisabled)) {
          return (
            <FinancialBeautifiedValue
              value={value}
              className={classNames(styles.textItem, styles.alignColumns, {
                [styles['total']]: isTotalRowsColumn(keyValue, isDisabled),
              })}
            />
          )
        }

        return (
          <FinancialInput
            showOnlyBeautifiedValue={isDisabled}
            type="number"
            className={styles.alignColumns}
            value={value || ''}
            onChange={handleNumberColumnChange({
              field: currentYearQuarter2Field,
              keyValue,
              onChange,
            })}
          />
        )
      },
    },
    {
      field: currentYearQuarter3Field,
      titleRenderFn: () =>
        getCurrentYearQuarterThView({ quarter: 3, currentQuarter: currentFQuarter, currentYear }),
      renderComponent: ({ value, keyValue }) => {
        if (isTotalRowsColumn(keyValue, isDisabled)) {
          return (
            <FinancialBeautifiedValue
              value={value}
              className={classNames(styles.textItem, styles.alignColumns, {
                [styles['total']]: isTotalRowsColumn(keyValue, isDisabled),
              })}
            />
          )
        }

        return (
          <FinancialInput
            showOnlyBeautifiedValue={isDisabled}
            type="number"
            value={value || ''}
            className={styles.alignColumns}
            onChange={handleNumberColumnChange({
              field: currentYearQuarter3Field,
              keyValue,
              onChange,
            })}
          />
        )
      },
    },
    {
      field: currentYearQuarter4Field,
      titleRenderFn: () =>
        getCurrentYearQuarterThView({ quarter: 4, currentQuarter: currentFQuarter, currentYear }),
      renderComponent: ({ value, keyValue }) => {
        if (isTotalRowsColumn(keyValue, isDisabled)) {
          return (
            <FinancialBeautifiedValue
              value={value}
              className={classNames(styles.textItem, styles.alignColumns, {
                [styles['total']]: isTotalRowsColumn(keyValue, isDisabled),
              })}
            />
          )
        }

        return (
          <FinancialInput
            showOnlyBeautifiedValue={isDisabled}
            type="number"
            value={value || ''}
            className={styles.alignColumns}
            onChange={handleNumberColumnChange({
              field: currentYearQuarter4Field,
              keyValue,
              onChange,
            })}
          />
        )
      },
    },
    {
      field: currentYearRevenueField,
      titleRenderFn: () => getCurrentYearThViewTotal(currentYear, currentFQuarter),
      renderComponent: ({ value, keyValue }) => (
        <RenderYearResultColumn
          isDisabled={isDisabled}
          keyValue={keyValue as TRevenueSnapshotKeys}
          isInconsistency={isInconsistency}
          yearField={currentYearRevenueField}
          quarterFields={[
            currentYearQuarter1Field,
            currentYearQuarter2Field,
            currentYearQuarter3Field,
            currentYearQuarter4Field,
          ]}
          value={value}
        />
      ),
    },
    {
      field: yoyField,
      titleRenderFn: () => {
        const isLastQuarter = currentFQuarter === 4

        return (
          <div className={styles.totalTh}>
            <Translation
              text={getFormedEltTranslation(
                isLastQuarter ? 'revenue-table-yoy-actuals' : 'revenue-table-yoy'
              )}
            />
          </div>
        )
      },
      renderComponent: ({ value, keyValue }) => {
        const isApprochableValue = ['string', 'number'].includes(typeof value)

        if (isTotalRowsColumn(keyValue, isDisabled)) {
          return (
            <p
              className={classNames(
                styles.textItem,
                styles.totalColumn,
                styles.column,
                styles.alignColumns,
                styles.yoy,
                {
                  [styles['total']]: isTotalRowsColumn(keyValue, isDisabled),
                }
              )}
            >
              {getPercentView(isApprochableValue ? Number(value) : null)}
            </p>
          )
        }

        return (
          <p className={classNames(styles.textItem, styles.alignColumns, styles.yoy)}>
            {getPercentView(isApprochableValue ? Number(value) : null)}
          </p>
        )
      },
    },
  ]
}
